import { formatDateShortDD, isAfter, isBefore, isValidDateString } from '~/services/TimeService';

import { Cart, CartRawItem } from '~/types/services/carts';

import { getCartItemInfo } from './cartItemInfo';

export function itemTypesLabel(items: Array<CartRawItem>): string {
  if (!items?.length) {
    return '—';
  }
  // A cart can contain multiple items of the same type (e.g. two hotel rooms),
  // so we de-duplicate before joining into a label.
  return [...new Set(items.map((item) => item.itemType))].join(', ');
}

// A cart can contain multiple items of the same type for one trip (e.g. two
// hotel rooms with 2 + 1 travellers), so we sum counts within each itemType.
// Different item types (hotel, flight, tour, ...) all represent the same
// group of travellers, so we take the max across types.
export function travellerCount(items: Array<CartRawItem>): number {
  const countsByType = (items ?? []).reduce<Map<string, number>>((acc, item) => {
    const count = getCartItemInfo(item).travellerCount;
    if (count > 0) {
      acc.set(item.itemType, (acc.get(item.itemType) ?? 0) + count);
    }
    return acc;
  }, new Map());

  return countsByType.size ? Math.max(...countsByType.values()) : 0;
}

export function travelStartDate(items: Array<CartRawItem>): string | null {
  const starts = (items ?? []).map((item) => getCartItemInfo(item).travelStart).filter(isValidDateString);

  if (!starts.length) {
    return null;
  }
  const min = starts.reduce((acc, curr) => (isBefore(curr, acc) ? curr : acc));
  return formatDateShortDD(min);
}

export function travelEndDate(items: Array<CartRawItem>): string | null {
  const ends = (items ?? []).map((item) => getCartItemInfo(item).travelEnd).filter(isValidDateString);

  if (!ends.length) {
    return null;
  }
  const max = ends.reduce((acc, curr) => (isAfter(curr, acc) ? curr : acc));
  return formatDateShortDD(max);
}

export function cartCreatedAtToDate(value: number | null): Date | null {
  // Backend returns `createdAt` as fractional epoch seconds
  return value ? new Date(value * 1000) : null;
}

export function cartRowId(cart: Cart): string {
  return cart.transactionKey;
}
